import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import {
  migrateLegacyStorageKey,
  namespacedStorageKey,
  resolveTenantAdminId,
} from '../lib/tenantStorage';
import { InventoryItem, Shipment, User, Subscription } from '../types';

interface CachedData {
  users: User[];
  inventory: InventoryItem[];
  shipments: Shipment[];
  lastUpdated: string;
}

interface DataCacheContextType {
  cachedData: CachedData;
  updateCache: (key: keyof CachedData, data: unknown) => void;
  clearCache: () => void;
  isDataStale: (maxAgeMinutes?: number) => boolean;
}

const DataCacheContext = createContext<DataCacheContextType | undefined>(undefined);

interface DataCacheProviderProps {
  children: ReactNode;
}

const initialCacheData: CachedData = {
  users: [],
  inventory: [],
  shipments: [],
  lastUpdated: new Date().toISOString(),
};

const LEGACY_CACHE_KEY = 'app_data_cache';

export const DataCacheProvider: React.FC<DataCacheProviderProps> = ({ children }) => {
  const { user, buyer } = useAuth();
  const tenantAdminId = resolveTenantAdminId(user, buyer);
  const storageKey = namespacedStorageKey(LEGACY_CACHE_KEY, tenantAdminId);

  const [cachedData, setCachedData] = useState<CachedData>(initialCacheData);

  useEffect(() => {
    if (!tenantAdminId) {
      setCachedData(initialCacheData);
      return;
    }
    migrateLegacyStorageKey(LEGACY_CACHE_KEY, storageKey);
    try {
      const raw = localStorage.getItem(storageKey);
      setCachedData(raw ? (JSON.parse(raw) as CachedData) : initialCacheData);
    } catch {
      setCachedData(initialCacheData);
    }
  }, [storageKey, tenantAdminId]);

  const persistCache = (next: CachedData) => {
    if (!tenantAdminId) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(next));
    } catch {
      // quota or disabled storage
    }
  };

  const updateCache = (key: keyof CachedData, data: unknown) => {
    if (key === 'lastUpdated') return;

    setCachedData((prev) => {
      const next = {
        ...prev,
        [key]: data,
        lastUpdated: new Date().toISOString(),
      };
      persistCache(next);
      return next;
    });
  };

  const clearCache = () => {
    setCachedData(initialCacheData);
    if (tenantAdminId) {
      try {
        localStorage.removeItem(storageKey);
      } catch {
        // ignore
      }
    }
  };

  const isDataStale = (maxAgeMinutes: number = 5): boolean => {
    if (!cachedData.lastUpdated) return true;

    const lastUpdate = new Date(cachedData.lastUpdated);
    const now = new Date();
    const ageMinutes = (now.getTime() - lastUpdate.getTime()) / (1000 * 60);

    return ageMinutes > maxAgeMinutes;
  };

  return (
    <DataCacheContext.Provider value={{
      cachedData,
      updateCache,
      clearCache,
      isDataStale,
    }}>
      {children}
    </DataCacheContext.Provider>
  );
};

export const useDataCache = () => {
  const context = useContext(DataCacheContext);
  if (!context) {
    throw new Error('useDataCache must be used within a DataCacheProvider');
  }
  return context;
};
